import { useFacilityNavigation } from '@/shared/lib/hooks/use-facility-navigation';
import { useMemo } from 'react';
import { useFacilityModeStore } from './facility-mode.store';

/**
 * Resolves the global facility mode.
 *
 * A page is in Single Mode when a facility is selected in the header dropdown
 * **or** the user can only access one facility (auto-selected, no dropdown
 * shown). Portfolio Mode is "All Facilities".
 */
export const useFacilityMode = () => {
  const { facilitiesOptions } = useFacilityNavigation();
  const selectedFacilityId = useFacilityModeStore(
    (state) => state.selectedFacilityId
  );
  const selectFacility = useFacilityModeStore((state) => state.selectFacility);
  const clearSelection = useFacilityModeStore((state) => state.clearSelection);

  const isSingleFacilityUser = facilitiesOptions?.length === 1;

  return useMemo(() => {
    // Single-access users are always in Single Mode on their one facility,
    // without needing anything persisted.
    const resolvedId = isSingleFacilityUser
      ? facilitiesOptions![0].value
      : selectedFacilityId;

    const selectedFacility =
      facilitiesOptions?.find((option) => option.value === resolvedId) ?? null;

    // A stale id (facility revoked between sessions) must not strand the user
    // in Single Mode on a facility they can no longer see.
    //
    // But only judge that once the options have actually loaded. Treating the
    // loading window as Portfolio Mode makes every scoped query fire twice --
    // once unscoped, then again once options arrive -- which both wastes a
    // request and flashes all-facility data before it narrows.
    const optionsLoaded = facilitiesOptions !== undefined;
    const isResolved = optionsLoaded
      ? Boolean(selectedFacility)
      : resolvedId != null;

    return {
      isSingleMode: isResolved,
      isSingleFacilityUser,
      selectedFacilityId: isResolved ? resolvedId : null,
      selectedFacility,
      facilitiesOptions,
      selectFacility,
      clearSelection,
    };
  }, [
    facilitiesOptions,
    isSingleFacilityUser,
    selectedFacilityId,
    selectFacility,
    clearSelection,
  ]);
};
